/**
 * <portfolio-lightbox> — Fullscreen gallery lightbox for portfolio articles.
 *
 * Usage:
 *   <portfolio-lightbox>
 *     <button data-lightbox-open data-index="0">…</button>
 *     <template data-lightbox-urls>url1||url2||url3</template>
 *   </portfolio-lightbox>
 */
class PortfolioLightbox extends HTMLElement {
  connectedCallback() {
    this.urls = (this.querySelector('[data-lightbox-urls]')?.content?.textContent || '')
      .split('||')
      .map(url => url.trim())
      .filter(Boolean);
    if (!this.urls.length) return;

    this.index = 0;
    this.dialog = null;

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-lightbox-open]');
      if (btn) {
        this.openedBy = btn;
        this.index = parseInt(btn.dataset.index, 10) || 0;
        this.open();
      }
    });
  }

  disconnectedCallback() {
    this.close();
  }

  open() {
    if (this.dialog) { this.close(); }

    const d = document.createElement('dialog');
    d.className = 'pav2-lb';
    d.innerHTML = [
      '<div class="pav2-lb__backdrop"></div>',
      '<div class="pav2-lb__wrap">',
      '  <button class="pav2-lb__close" aria-label="Close">&times;</button>',
      '  <button class="pav2-lb__arrow pav2-lb__arrow--prev" aria-label="Previous">\u2039</button>',
      '  <img class="pav2-lb__img" alt="">',
      '  <button class="pav2-lb__arrow pav2-lb__arrow--next" aria-label="Next">\u203A</button>',
      '  <span class="pav2-lb__counter"></span>',
      '</div>'
    ].join('');
    document.body.appendChild(d);
    this.dialog = d;

    this.img = d.querySelector('.pav2-lb__img');
    this.counter = d.querySelector('.pav2-lb__counter');

    d.querySelector('.pav2-lb__close').addEventListener('click', () => this.close());
    d.querySelector('.pav2-lb__backdrop').addEventListener('click', () => this.close());
    d.querySelector('.pav2-lb__arrow--prev').addEventListener('click', () => this.go(-1));
    d.querySelector('.pav2-lb__arrow--next').addEventListener('click', () => this.go(1));

    this._onKey = (e) => {
      if (e.key === 'ArrowLeft') this.go(-1);
      if (e.key === 'ArrowRight') this.go(1);
    };
    document.addEventListener('keydown', this._onKey);

    d.addEventListener('close', () => {
      document.removeEventListener('keydown', this._onKey);
      d.remove();
      this.dialog = null;
      if (this.openedBy) {
        this.openedBy.focus();
        this.openedBy = null;
      }
    });

    d.showModal();
    this.show();
  }

  close() {
    this.dialog?.close();
  }

  go(dir) {
    this.index = (this.index + dir + this.urls.length) % this.urls.length;
    this.show();
  }

  show() {
    const url = this.urls[this.index];
    if (!url) return;
    this.img.src = url.replace(/width=\d+/, 'width=1600');
    const originalImg = this.querySelector('[data-index="' + this.index + '"] img');
    this.img.alt = originalImg?.alt || 'Gallery image ' + (this.index + 1);
    this.counter.textContent = (this.index + 1) + ' / ' + this.urls.length;
  }
}

if (!customElements.get('portfolio-lightbox')) {
  customElements.define('portfolio-lightbox', PortfolioLightbox);
}
